/**
 * Route-visibility matrix — a 1:1 encoding of docs/ROLE-MATRIX.md §3 (the
 * ratified joint contract, #595). A jest parity test
 * (src/rbac/__tests__/roleMatrixParity.test.ts) parses that markdown and
 * asserts this file matches it exactly — doc drift breaks CI.
 *
 * Mechanics (longest-prefix matching, homeRoute, Access-Denied-not-blank) are
 * ported from the design kit's rbac.js; the DATA is not — the kit's 15-role
 * GUARDS table is a superseded artifact and must never be transplanted.
 *
 * Differences from the kit's mechanics, on purpose:
 *  - FAIL-CLOSED on unmatched routes (kit's canAccess is fail-open).
 *  - FAIL-CLOSED on unknown/legacy roles (kit passes unknown roles through).
 *  - Keys are this SPA's BrowserRouter paths, not the kit's hash routes.
 *
 * ROLE-MATRIX §4.1: reseller routes get NO entries here — "no lane may ship
 * any gate for these routes" until the core-side role exists. Unknown routes
 * (including /reseller/*) are denied for everyone by the fail-closed default,
 * which is the absence of a gate, not a gate.
 *
 * NOT YET WIRED into src/routes/index.tsx — the live swap is Stage 3 (#601),
 * blocked on the legacy-role mapping ratification (#641).
 */

import {
  CANONICAL_ROLES,
  CanonicalRole,
  normalizeRole,
  RoleDenialReason,
} from "./roles";

export type RoleAccess = Readonly<Record<CanonicalRole, boolean>>;

export interface ScreenEntry {
  /** Row number in ROLE-MATRIX §3. */
  id: number;
  /** Screen name exactly as printed in ROLE-MATRIX §3. */
  screen: string;
  /** Route tokens exactly as printed in the doc's Route cell (hash routes). */
  docRoutes: readonly string[];
  /** Translated SPA (BrowserRouter) path prefixes. Empty = not routable yet. */
  spaPaths: readonly string[];
  /** Per-role visibility; null = pre-authentication row (Login). */
  access: RoleAccess | null;
}

function grant(...roles: CanonicalRole[]): RoleAccess {
  const allowed = new Set<CanonicalRole>(roles);
  const access = {} as Record<CanonicalRole, boolean>;
  for (const role of CANONICAL_ROLES) {
    access[role] = allowed.has(role);
  }
  return access;
}

const ALL = grant(...CANONICAL_ROLES);

/**
 * Kit hash route → this SPA's BrowserRouter path. The only genuine renames are
 * `#/` → `/dashboard` (RootRedirect owns `/`) and `#/groups*` → `/data-groups*`
 * (src/routes/index.tsx). Everything else is a `#` strip.
 */
export function translateDocRoute(docRoute: string): string {
  if (docRoute === "#/") return "/dashboard";
  if (docRoute === "#/groups" || docRoute.startsWith("#/groups/")) {
    return `/data-groups${docRoute.slice("#/groups".length)}`;
  }
  return docRoute.slice(1);
}

function entry(
  id: number,
  screen: string,
  docRoutes: readonly string[],
  access: RoleAccess | null,
): ScreenEntry {
  return {
    id,
    screen,
    docRoutes,
    spaPaths: docRoutes.map(translateDocRoute),
    access,
  };
}

export const SCREEN_MATRIX: readonly ScreenEntry[] = [
  // ── §3.1 Core NU DATA (8) ──
  entry(1, "Dashboard", ["#/"], ALL),
  entry(
    2,
    "He-Man Search",
    ["#/search"],
    grant("owner", "admin", "member", "tenant_admin"),
  ),
  entry(
    3,
    "Analytics",
    ["#/analytics"],
    grant("owner", "admin", "member", "viewer", "tenant_admin"),
  ),
  entry(
    4,
    "List Management",
    ["#/lists", "#/lists/analytics"],
    grant("owner", "admin", "member", "viewer", "tenant_admin"),
  ),
  entry(
    5,
    "Data Enrichment",
    ["#/enrichment", "#/enrichment-pipeline"],
    grant("owner", "admin", "member", "tenant_admin"),
  ),
  entry(
    6,
    "Data Groups",
    ["#/groups", "#/groups/analytics"],
    grant("owner", "admin", "member", "viewer", "tenant_admin"),
  ),
  entry(
    7,
    "Connections",
    ["#/connections", "#/connections/mapping"],
    grant("owner", "admin", "member", "tenant_admin"),
  ),
  entry(
    8,
    "Data Export",
    ["#/export"],
    grant("owner", "admin", "member", "tenant_admin"),
  ),
  // ── §3.2 Added from the real product (4) ──
  entry(
    9,
    "Enrichment Plans",
    ["#/plans"],
    grant("owner", "admin", "member", "tenant_admin"),
  ),
  entry(
    10,
    "Campaigns",
    ["#/campaigns"],
    grant("owner", "admin", "member", "viewer", "tenant_admin"),
  ),
  entry(
    11,
    "Suppression",
    ["#/suppression"],
    grant("owner", "admin", "tenant_admin"),
  ),
  entry(
    12,
    "API Usage",
    ["#/admin/api-usage"],
    grant("owner", "admin", "billing", "tenant_admin"),
  ),
  // ── §3.3 Shared admin (13) ──
  entry(
    13,
    "Tenants",
    ["#/admin/tenants"],
    grant("owner", "admin", "tenant_admin"),
  ),
  entry(
    14,
    "Users",
    ["#/admin/users"],
    grant("owner", "admin", "tenant_admin"),
  ),
  entry(
    15,
    "Roles",
    ["#/admin/roles"],
    grant("owner", "admin", "tenant_admin"),
  ),
  entry(
    16,
    "Audit",
    ["#/admin/audit"],
    grant("owner", "admin", "tenant_admin"),
  ),
  entry(17, "Account Settings", [], ALL),
  entry(
    18,
    "API Tokens",
    [],
    grant("owner", "admin", "member", "tenant_admin"),
  ),
  entry(19, "Help", ["#/help"], ALL),
  entry(20, "Notifications", [], ALL),
  entry(21, "Onboarding", [], ALL),
  entry(22, "Profile", ["#/profile"], ALL),
  entry(23, "Search Results", [], ALL),
  entry(24, "Route Not Found (404)", [], ALL),
  entry(25, "Login", ["#/login"], null),
];

/** Flattened [prefix, entry] table, longest prefix first (first match wins). */
const ROUTE_TABLE: ReadonlyArray<readonly [string, ScreenEntry]> =
  SCREEN_MATRIX.flatMap((screen) =>
    screen.spaPaths.map((prefix) => [prefix, screen] as const),
  ).sort((a, b) => b[0].length - a[0].length);

function normalizePath(path: string): string {
  let p = path.split(/[?#]/)[0];
  if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
  return p;
}

/** Longest-prefix route match at path-segment boundaries. */
export function matchScreen(path: string): ScreenEntry | null {
  const p = normalizePath(path);
  const hit = ROUTE_TABLE.find(
    ([prefix]) => p === prefix || p.startsWith(`${prefix}/`),
  );
  return hit ? hit[1] : null;
}

export type AccessDenialReason =
  RoleDenialReason | "UNKNOWN_ROUTE" | "ROLE_DENIED";

export type AccessDecision =
  | { allowed: true; role: CanonicalRole | null; screen: ScreenEntry }
  | { allowed: false; reason: AccessDenialReason; role: CanonicalRole | null };

/**
 * FAIL-CLOSED access check.
 *  - unknown/legacy/absent role → denied (reason from normalizeRole)
 *  - route with no matrix entry → denied ('UNKNOWN_ROUTE')
 *  - matched route, role not granted → denied ('ROLE_DENIED')
 * Pre-authentication rows (Login) are the only allow without a valid role.
 */
export function canAccess(
  role: string | null | undefined,
  path: string,
): AccessDecision {
  const screen = matchScreen(path);
  const normalized = normalizeRole(role);
  const access = screen ? screen.access : undefined;

  // Pre-authentication screens are reachable regardless of role state.
  if (screen && access === null) {
    return {
      allowed: true,
      role: normalized.ok ? normalized.role : null,
      screen,
    };
  }
  if (!normalized.ok) {
    return { allowed: false, reason: normalized.reason, role: null };
  }
  if (!screen || !access) {
    return { allowed: false, reason: "UNKNOWN_ROUTE", role: normalized.role };
  }
  if (!access[normalized.role]) {
    return { allowed: false, reason: "ROLE_DENIED", role: normalized.role };
  }
  return { allowed: true, role: normalized.role, screen };
}

/**
 * Role-aware landing route (kit homeRoute mechanic). Per ROLE-MATRIX §3 row 1
 * every canonical role can see the Dashboard, so all six land there today; the
 * fallback scan keeps this correct if a future matrix revision closes row 1
 * for some role. Invalid/legacy roles land on /login (fail-closed).
 */
export function homeRoute(role: string | null | undefined): string {
  const normalized = normalizeRole(role);
  if (!normalized.ok) return "/login";
  if (canAccess(role, "/dashboard").allowed) return "/dashboard";
  const first = SCREEN_MATRIX.find(
    (screen) =>
      screen.spaPaths.length > 0 &&
      screen.access !== null &&
      screen.access[normalized.role],
  );
  return first ? first.spaPaths[0] : "/login";
}
